import React, { useState } from 'react';
import { Wrench, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Ticket } from './TicketManagement';
import TicketManagement from './TicketManagement';

interface FacilitiesManagementProps {
  tickets: Ticket[];
}

const FacilitiesManagement: React.FC<FacilitiesManagementProps> = ({ tickets }) => {
  const [laborRate, setLaborRate] = useState(32);

  const cleaningZones = [
    {
      id: 'north-wing',
      name: 'North Wing Corridor',
      x: 50,
      y: 50,
      width: 200,
      height: 120,
      paths: 412,
      threshold: 350,
      status: 'priority',
      lastCleaned: '8 hours ago',
      estimatedMinutes: 45
    },
    {
      id: 'break-room',
      name: 'Break Room A',
      x: 300,
      y: 50,
      width: 150,
      height: 120,
      paths: 334,
      threshold: 300,
      status: 'priority',
      lastCleaned: '6 hours ago',
      estimatedMinutes: 30
    },
    {
      id: 'main-lobby',
      name: 'Main Lobby',
      x: 500,
      y: 50,
      width: 170,
      height: 120,
      paths: 287,
      threshold: 400,
      status: 'ok',
      lastCleaned: '4 hours ago',
      estimatedMinutes: 35
    },
    {
      id: 'open-area',
      name: 'Open Office Area',
      x: 50,
      y: 220,
      width: 300,
      height: 180,
      paths: 268,
      threshold: 450,
      status: 'ok',
      lastCleaned: '5 hours ago',
      estimatedMinutes: 55
    },
    {
      id: 'conf-wing',
      name: 'Conference Wing',
      x: 400,
      y: 220,
      width: 140,
      height: 90,
      paths: 124,
      threshold: 200,
      status: 'ok',
      lastCleaned: '3 hours ago',
      estimatedMinutes: 25
    },
    {
      id: 'east-wing',
      name: 'East Wing',
      x: 400,
      y: 330,
      width: 270,
      height: 70,
      paths: 18,
      threshold: 250,
      status: 'skip',
      lastCleaned: '2 days ago',
      estimatedMinutes: 40
    }
  ];

  const auditRecords = [
    { zone: 'North Wing', cleaned: '2:30 PM', pathLoad: 412, compliant: true },
    { zone: 'Break Room A', cleaned: '3:15 PM', pathLoad: 334, compliant: true },
    { zone: 'South Wing', cleaned: '4:00 PM', pathLoad: 156, compliant: true }
  ];

  const handleUpdateTicket = (ticketId: string, updates: Partial<Ticket>) => {
    console.log('Update ticket:', ticketId, updates);
  };

  const handleDeleteTicket = (ticketId: string) => {
    console.log('Delete ticket:', ticketId);
  };

  const getZoneColor = (zone: typeof cleaningZones[0]) => {
    if (zone.status === 'priority') return '#0A9B9Bcc';
    if (zone.status === 'skip') return '#F9F7F2';
    return '#0A9B9B44';
  };

  const priorityZones = cleaningZones.filter(z => z.status === 'priority');
  const skipZones = cleaningZones.filter(z => z.status === 'skip');
  const totalMinutes = priorityZones.reduce((sum, z) => sum + z.estimatedMinutes, 0);

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Facilities Care
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Cleaning demand from presence and path load. Sample building data, illustrative
        </p>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{ backgroundColor: '#fee', borderColor: '#fcc' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={18} style={{ color: '#c33' }} />
            <span className="text-sm" style={{ color: '#c33' }}>Priority Zones</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: '#c33' }}>{priorityZones.length}</div>
          <div className="text-xs" style={{ color: '#c33' }}>need cleaning now</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Est. Labor</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{totalMinutes} min</div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>for priority zones</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Wrench size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Active Paths</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>1,443</div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>today across zones</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-badge-bg)', borderColor: 'var(--sca-teal-muted)' }}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-teal)' }}>Efficiency Gain</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-teal)' }}>34%</div>
          <div className="text-xs" style={{ color: 'var(--sca-teal)' }}>labor saved vs fixed schedule</div>
        </div>
      </div>

      {/* Hero: Cleaning Demand Map */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
            Cleaning Demand Map: Floor 3
          </h3>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Path load intensity drives tonight's cleaning routes. Skip empty zones, prioritize high-traffic corridors.
          </p>
        </div>

        <svg width="700" height="420" style={{ border: '1px solid #e8e4dc', borderRadius: '8px', backgroundColor: '#F9F7F2' }}>
          {cleaningZones.map((zone) => {
            const fillColor = getZoneColor(zone);
            
            return (
              <g key={zone.id}>
                <rect
                  x={zone.x}
                  y={zone.y}
                  width={zone.width}
                  height={zone.height}
                  fill={fillColor}
                  stroke="#1a1a1a"
                  strokeWidth={1}
                />

                <text
                  x={zone.x + zone.width / 2}
                  y={zone.y + zone.height / 2 - 10}
                  textAnchor="middle"
                  style={{ fontSize: '12px', fontWeight: '600', fill: '#1a1a1a' }}
                >
                  {zone.name}
                </text>

                <text
                  x={zone.x + zone.width / 2}
                  y={zone.y + zone.height / 2 + 8}
                  textAnchor="middle"
                  style={{ fontSize: '14px', fontWeight: '700', fill: zone.status === 'priority' ? '#c33' : '#666' }}
                >
                  {zone.paths} paths
                </text>

                <text
                  x={zone.x + zone.width / 2}
                  y={zone.y + zone.height / 2 + 24}
                  textAnchor="middle"
                  style={{ fontSize: '10px', fill: '#666' }}
                >
                  {zone.status === 'priority' ? 'Clean now' : zone.status === 'skip' ? 'Skip' : 'OK'}
                </text>
              </g>
            );
          })}
        </svg>

        <div className="mt-4 flex items-center gap-6 text-xs" style={{ color: 'var(--sca-muted)' }}>
          <div className="flex items-center gap-2">
            <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9Bcc' }}></div>
            <span>Priority (clean now)</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B44' }}></div>
            <span>OK (within threshold)</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 16, height: 16, backgroundColor: '#F9F7F2', border: '1px solid #1a1a1a' }}></div>
            <span>Skip (empty zone)</span>
          </div>
        </div>
      </div>

      {/* Route Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Priority */}
        <div className="rounded-lg border p-4" style={{ backgroundColor: '#fee', borderColor: '#fcc' }}>
          <h3 className="text-lg font-semibold mb-3" style={{ color: '#c33' }}>
            Priority: Clean Now
          </h3>
          <div className="space-y-3">
            {priorityZones.map((zone) => (
              <div key={zone.id} className="p-3 rounded-lg" style={{ backgroundColor: 'white' }}>
                <div className="font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>{zone.name}</div>
                <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>
                  {zone.paths} paths / {zone.threshold} threshold
                </div>
                <div className="flex justify-between text-xs" style={{ color: 'var(--sca-muted)' }}>
                  <span>Last: {zone.lastCleaned}</span>
                  <span>{zone.estimatedMinutes} min</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-sm font-semibold" style={{ color: '#c33' }}>
            Total: {totalMinutes} minutes
          </div>
        </div>

        {/* Skip */}
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
            Skip: Empty Zones
          </h3>
          <div className="space-y-3">
            {skipZones.map((zone) => (
              <div key={zone.id} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
                <div className="font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>{zone.name}</div>
                <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>
                  {zone.paths} paths / {zone.threshold} threshold
                </div>
                <div className="text-xs" style={{ color: 'var(--sca-teal)' }}>
                  Save {zone.estimatedMinutes} min labor
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
            Labor saved: {skipZones.reduce((sum, z) => sum + z.estimatedMinutes, 0)} min
          </div>
        </div>

        {/* In Progress */}
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
            In Progress
          </h3>
          <div className="space-y-3">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-badge-bg)' }}>
              <div className="font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>South Wing</div>
              <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>
                Started 2:45 PM
              </div>
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--sca-teal)' }}>
                <Clock size={12} />
                <span>Est. complete: 3:20 PM</span>
              </div>
            </div>

            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              <strong style={{ color: 'var(--sca-ink)' }}>Rationale:</strong> 298 paths detected, above 250 threshold. Compliance with presence-based SLA.
            </div>
          </div>
        </div>
      </div>

      {/* Cleaning Contract Renegotiation Pack */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
        <div className="mb-4">
          <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>CONTRACT RENEGOTIATION PACK</div>
          <h3 className="text-xl font-semibold" style={{ color: 'var(--sca-ink)' }}>
            Vendor Negotiation Artifact
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--sca-muted)' }}>
            Occupancy-backed clean vs skip evidence for facilities leadership. Sample data for negotiation demo.
          </p>
        </div>

        {/* Labor Minutes Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
            <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
              Static Nightly Scope (Current Contract)
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Total Zones</div>
                <div className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                  {cleaningZones.length}
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Labor Minutes</div>
                <div className="text-2xl font-bold" style={{ color: '#c33' }}>
                  {cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0)} min
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Nightly Cost at ${laborRate}/hr</div>
                <div className="text-2xl font-bold" style={{ color: '#c33' }}>
                  ${((cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
            <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
              Presence-Driven Scope (Proposed)
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Priority + OK Zones</div>
                <div className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                  {cleaningZones.filter(z => z.status !== 'skip').length}
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Labor Minutes</div>
                <div className="text-2xl font-bold" style={{ color: '#0A9B9B' }}>
                  {cleaningZones.filter(z => z.status !== 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0)} min
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Nightly Cost at ${laborRate}/hr</div>
                <div className="text-2xl font-bold" style={{ color: '#0A9B9B' }}>
                  ${((cleaningZones.filter(z => z.status !== 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delta Analysis */}
        <div className="rounded-lg border p-4 mb-6" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
          <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
            Contract Delta (Annual)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Labor Savings per Night</div>
              <div className="text-xl font-bold" style={{ color: '#0A9B9B' }}>
                {cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0)} min
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Nightly Cost Reduction</div>
              <div className="text-xl font-bold" style={{ color: '#0A9B9B' }}>
                ${((cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate).toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Annual Savings (250 days)</div>
              <div className="text-xl font-bold" style={{ color: '#0A9B9B' }}>
                ${(((cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate) * 250 / 1000).toFixed(1)}K
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Efficiency Gain</div>
              <div className="text-xl font-bold" style={{ color: '#0A9B9B' }}>
                {((cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0)) * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs" style={{ color: 'var(--sca-muted)' }}>Adjust blended labor rate ($/hr):</label>
            <input
              type="number"
              value={laborRate}
              onChange={(e) => setLaborRate(parseInt(e.target.value) || 32)}
              className="w-full mt-1 px-3 py-2 text-sm rounded border"
              style={{ borderColor: 'var(--sca-border)' }}
            />
          </div>
        </div>

        {/* Clean/Skip/Priority Breakdown */}
        <div className="rounded-lg border p-4 mb-6" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
          <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
            Clean / Skip / Priority Breakdown with Presence Rationale
          </div>
          <div className="space-y-2 text-sm">
            {cleaningZones.map((zone) => (
              <div key={zone.id} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: zone.status === 'priority' ? '#fee' : zone.status === 'skip' ? 'var(--sca-hover)' : 'var(--sca-badge-bg)' }}>
                <div className="flex items-center gap-3">
                  <div className="font-medium" style={{ color: 'var(--sca-ink)', minWidth: 160 }}>{zone.name}</div>
                  <div className="text-xs px-2 py-1 rounded font-semibold" style={{
                    backgroundColor: zone.status === 'priority' ? '#c33' : zone.status === 'skip' ? '#666' : '#0A9B9B',
                    color: 'white'
                  }}>
                    {zone.status === 'priority' ? 'PRIORITY' : zone.status === 'skip' ? 'SKIP' : 'CLEAN'}
                  </div>
                </div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)', maxWidth: 300 }}>
                  <strong style={{ color: 'var(--sca-ink)' }}>Rationale:</strong> {
                    zone.status === 'priority' ? `${zone.paths} paths (above ${zone.threshold} threshold), clean now` :
                    zone.status === 'skip' ? `${zone.paths} paths (below ${zone.threshold} threshold), empty zone, skip tonight` :
                    `${zone.paths} paths (within threshold), clean per schedule`
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* One-Page Summary */}
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
          <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
            Renegotiation Pack Summary (One-Page Talking Points)
          </div>
          <div className="space-y-3 text-sm" style={{ color: 'var(--sca-ink)' }}>
            <p>
              <strong>Situation:</strong> Current contract cleans {cleaningZones.length} zones nightly ({cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0)} min) 
              regardless of occupancy. Static scope = ${((cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate * 250 / 1000).toFixed(0)}K/year.
            </p>
            <p>
              <strong>Evidence:</strong> Floor sensor path load shows {cleaningZones.filter(z => z.status === 'skip').length} zones consistently empty overnight (below path threshold for 6+ hours). 
              Cleaning empty zones wastes labor and provides no resident value.
            </p>
            <p>
              <strong>Proposed Model:</strong> Dynamic scope driven by presence. Clean only priority ({priorityZones.length} zones tonight) and occupied zones. 
              Skip empty zones. Reallocate labor savings to deep-clean rotation or peak-hour restocking.
            </p>
            <p>
              <strong>Financial Impact:</strong> Annual savings of ${(((cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / 60) * laborRate) * 250 / 1000).toFixed(1)}K 
              at ${laborRate}/hr blended rate ({((cleaningZones.filter(z => z.status === 'skip').reduce((sum, z) => sum + z.estimatedMinutes, 0) / cleaningZones.reduce((sum, z) => sum + z.estimatedMinutes, 0)) * 100).toFixed(0)}% efficiency gain). 
              No occupant impact; cleanliness maintained where presence occurs.
            </p>
            <p>
              <strong>Ask to Vendor:</strong> Renegotiate to presence-based SLA with monthly true-up. FM pays for zones with presence + priority spills. 
              Vendor keeps labor savings from skipped zones as incentive. Digital audit trail provides proof of compliance (zone had presence → cleaned within SLA).
            </p>
          </div>
        </div>

        <div className="mt-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
          Sample building data for illustrative vendor negotiation pack. Adjust labor rate to match local FM contract.
        </div>
      </div>

      {/* Audit Strip */}
      <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-badge-bg)', borderColor: 'var(--sca-teal-muted)' }}>
        <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
          Audit Trail: Today
        </h3>
        <div className="space-y-2">
          {auditRecords.map((record, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-3">
                <CheckCircle size={14} style={{ color: 'var(--sca-teal)' }} />
                <span style={{ color: 'var(--sca-ink)' }}>{record.zone}</span>
              </div>
              <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
                <span>Cleaned: {record.cleaned}</span>
                <span>Load: {record.pathLoad} paths</span>
                <span className="px-2 py-0.5 rounded" style={{
                  backgroundColor: 'var(--sca-teal)',
                  color: 'white'
                }}>
                  Compliant
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs" style={{ color: 'var(--sca-muted)' }}>
          Digital proof: zone had presence → cleaned within SLA window. Export for FM partner narrative.
        </div>
      </div>

      {/* Tickets Section (Secondary) */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Active Tickets
        </h3>
        <TicketManagement
          tickets={tickets}
          onUpdateTicket={handleUpdateTicket}
          onDeleteTicket={handleDeleteTicket}
        />
      </div>

      {/* Automation Info */}
      <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--sca-hover)' }}>
        <div className="text-sm font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Automation Rules
        </div>
        <div className="text-xs space-y-1" style={{ color: 'var(--sca-muted)' }}>
          <p><strong>Priority trigger:</strong> Zone exceeds path threshold (corridors: 350, offices: 200, break rooms: 300)</p>
          <p><strong>Skip logic:</strong> Zone below threshold for 6+ hours → skip and reallocate labor</p>
          <p><strong>SLA compliance:</strong> Clean within 4 hours of priority trigger</p>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesManagement;
